<template>
  <div class="page-container">
    <!-- 日期选择器区域 -->
    <div class="date-selector-container">
      <div class="date-selectors">
        <el-select
          v-model="selectedPeriod"
          placeholder="选择时间段"
          :clearable="false"
          style="width: 180px; margin-right: 12px;"
          @change="handlePeriodChange"
        >
          <el-option
            v-for="option in halfYearOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
        <el-select
          v-model="selectedTeam"
          placeholder="选择团队"
          clearable
          style="width: 160px; margin-left: 12px;"
          @change="handleTeamFilterChange"
        >
          <el-option
            label="全部团队"
            value=""
          />
          <el-option
            v-for="team in teamOptions"
            :key="team.value"
            :label="team.label"
            :value="team.value"
          />
        </el-select>
        <el-button
          type="success"
          @click="exportToExcel"
          :loading="exporting || loading"
          :disabled="!tableRows || tableRows.length === 0"
          style="margin-left: 12px;"
        >
          <i class="el-icon-download" style="margin-right: 4px;"></i>
          导出Excel
        </el-button>
      </div>
    </div>

    <div class="table-container">
      <div class="performance-table-container">
        <!-- 数据信息浮层 -->
        <div class="data-overlay">
          <div class="info-item">
            <span class="label">数据截止：</span>
            <span class="value">{{ dataDeadline }}</span>
          </div>
          <div class="info-item">
            <span class="label">半年时间进度：</span>
            <span class="value">{{ halfYearProgress }}%</span>
          </div>
        </div>

        <div class="page-title">
          <h2>{{ dynamicTitle }}</h2>
        </div>

        <div class="page-header">
        </div>

        <!-- Element UI 表格 -->
        <div class="el-table-wrapper">
          <el-table
            :data="tableRows"
            v-loading="loading"
            border
            height="100%"
            :row-class-name="getRowClassName"
            :cell-class-name="getCellClassName"
            style="width: 100%"
            :span-method="spanMethod"
          >
            <!-- 固定列 -->
            <el-table-column
              prop="teamName"
              label="团队名称"
              width="120"
              fixed="left"
              align="center"
              class-name="basic-info"
            >
              <template slot-scope="scope">
                <span :style="{ color: scope.row.teamColor || '#000000' }">
                  {{ scope.row.teamName || '-' }}
                </span>
              </template>
            </el-table-column>

            <el-table-column
              prop="teamLeader"
              label="队长"
              width="120"
              fixed="left"
              align="center"
              class-name="basic-info"
            >
              <template slot-scope="scope">
                {{ scope.row.teamLeader || '-' }}
              </template>
            </el-table-column>

            <el-table-column
              prop="groupLeader"
              label="组长"
              width="100"
              fixed="left"
              align="center"
              class-name="basic-info"
            >
              <template slot-scope="scope">
                <span
                  :class="{
                    'group-leader-style': scope.row.personType === 'group_leader',
                    'team-leader-style': scope.row.personType === 'team_leader',
                    'no-special-style': !scope.row.groupLeader || scope.row.groupLeader === '-'
                  }"
                  :style="getGroupLeaderCellStyle(scope.row)"
                >
                  {{ scope.row.groupLeader || '-' }}
                </span>
              </template>
            </el-table-column>

            <el-table-column
              prop="teamMembers"
              label="组员"
              min-width="120"
              fixed="left"
              align="center"
              class-name="basic-info"
            >
              <template slot-scope="scope">
                <el-tooltip
                  :content="scope.row.teamMembers || ''"
                  placement="top"
                  :disabled="!scope.row.teamMembers"
                  :open-delay="500"
                >
                  <span>
                    {{ scope.row.teamMembers || '-' }}
                  </span>
                </el-tooltip>
              </template>
            </el-table-column>

            <el-table-column
              prop="merchandiser"
              label="跟单员"
              width="100"
              align="center"
              class-name="basic-info"
            >
              <template slot-scope="scope">
                {{ scope.row.merchandiser || '-' }}
              </template>
            </el-table-column>

            <el-table-column
              prop="halfYearPeriod"
              label="半年度"
              width="80"
              align="center"
              class-name="basic-info"
            >
              <template slot-scope="scope">
                {{ scope.row.halfYearPeriod || '-' }}
              </template>
            </el-table-column>

            <!-- 团队业绩列组 -->
            <el-table-column label="团队业绩" align="center" class-name="team-performance">
              <el-table-column
                prop="teamHalfYearOrderTarget"
                label="团队目标"
                min-width="120"
                align="center"
                class-name="team-performance"
              >
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.teamHalfYearOrderTarget) }}
                </template>
              </el-table-column>

              <el-table-column
                prop="teamHalfYearOrderActual"
                label="团队达成"
                min-width="120"
                align="center"
                class-name="team-performance"
              >
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.teamHalfYearOrderActual) }}
                </template>
              </el-table-column>

              <el-table-column
                prop="teamHalfYearAchievementRate"
                label="团队达成率"
                width="100"
                align="center"
                class-name="team-performance"
              >
                <template slot-scope="scope">
                  {{ formatPercentage(scope.row.teamHalfYearAchievementRate) }}
                </template>
              </el-table-column>
            </el-table-column>

            <!-- 小组业绩列组 -->
            <el-table-column label="小组业绩" align="center" class-name="group-performance">
              <el-table-column
                prop="groupHalfYearOrderTarget"
                label="小组目标"
                min-width="120"
                align="center"
                class-name="group-performance"
              >
                <template slot-scope="scope">
                  <span v-if="scope.row.personType === 'team_leader'">-</span>
                  <span v-else>{{ formatCurrency(scope.row.groupHalfYearOrderTarget) }}</span>
                </template>
              </el-table-column>

              <el-table-column
                prop="groupHalfYearOrderActual"
                label="小组达成"
                min-width="120"
                align="center"
                class-name="group-performance"
              >
                <template slot-scope="scope">
                  <span v-if="scope.row.personType === 'team_leader'">-</span>
                  <span v-else>{{ formatCurrency(scope.row.groupHalfYearOrderActual) }}</span>
                </template>
              </el-table-column>

              <el-table-column
                prop="groupHalfYearAchievementRate"
                label="小组达成率"
                width="100"
                align="center"
                class-name="group-performance"
              >
                <template slot-scope="scope">
                  <span v-if="scope.row.personType === 'team_leader'">-</span>
                  <span v-else>{{ formatPercentage(scope.row.groupHalfYearAchievementRate) }}</span>
                </template>
              </el-table-column>
            </el-table-column>

            <!-- 个人业绩列组 -->
            <el-table-column label="个人业绩" align="center" class-name="core-performance">
              <el-table-column
                prop="halfYearOrderTarget"
                label="半年度目标"
                min-width="120"
                align="center"
                class-name="core-performance"
              >
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.halfYearOrderTarget) }}
                </template>
              </el-table-column>

              <el-table-column
                prop="halfYearOrderActual"
                label="半年度达成"
                min-width="120"
                align="center"
                class-name="core-performance"
                header-class-name="core-performance"
              >
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.halfYearOrderActual) }}
                </template>
              </el-table-column>

              <el-table-column
                prop="halfYearAchievementRate"
                label="半年达成率"
                width="100"
                align="center"
                class-name="core-performance"
                header-class-name="core-performance"
              >
                <template slot-scope="scope">
                  {{ formatPercentage(scope.row.halfYearAchievementRate) }}
                </template>
              </el-table-column>

              <el-table-column
                prop="ranking"
                label="排名"
                width="80"
                align="center"
                class-name="core-performance"
              >
                <template slot-scope="scope">
                  {{ scope.row.ranking || '' }}
                </template>
              </el-table-column>

              <el-table-column
                prop="gapTarget"
                label="缺口目标"
                min-width="120"
                align="center"
                class-name="gap-target"
                header-class-name="gap-target"
              >
                <template slot-scope="scope">
                  {{ formatGapTarget(scope.row.gapTarget) }}
                </template>
              </el-table-column>
            </el-table-column>

            <!-- 客户分析列组 -->
            <el-table-column label="客户分析" align="center" class-name="customer-analysis">
              <el-table-column
                prop="newCustomerAchievement"
                label="新客户达成"
                min-width="120"
                align="center"
                class-name="customer-analysis"
              >
                <template slot-scope="scope">
                  {{ formatCurrency(scope.row.newCustomerAchievement) }}
                </template>
              </el-table-column>

              <el-table-column
                prop="newCustomerRatio"
                label="新客户占比"
                min-width="120"
                align="center"
                class-name="customer-analysis"
              >
                <template slot-scope="scope">
                  {{ formatPercentage(scope.row.newCustomerRatio) }}
                </template>
              </el-table-column>

              <el-table-column
                prop="oldCustomerRatio"
                label="老客户占比"
                min-width="120"
                align="center"
                class-name="customer-analysis"
              >
                <template slot-scope="scope">
                  {{ formatPercentage(scope.row.oldCustomerRatio) }}
                </template>
              </el-table-column>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator'
import { request } from 'beans-admin-plugin'
import moment from 'moment'
import { PerformanceCalculationUtils } from './calculation-utils'
import * as XLSX from 'xlsx'

@Component({
  watch: {
    tableRows: {
      handler() {
        this.$nextTick(() => {
          // Element UI 表格自动处理高度，不需要手动同步
        })
      },
      deep: true
    }
  }
})
export default class HalfYearPerformanceTarget extends Vue {
  // 数据状态
  loading = false
  exporting = false // 导出状态
  rawData = [] // 原始数据
  tableRows = [] // 表格行数据
  allTableRows = [] // 所有表格行数据（未过滤）
  weekColumns = [] // 周度列定义（半年目标不使用，但保持一致性）
  queryDate = moment().format('YYYY-MM-DD') // 查询日期，默认当前月份
  updateTime = ''

  // 日期选择器状态
  selectedPeriod = this.getCurrentHalfYearOption() // 选中的时间段
  selectedTeam = '' // 选中的团队

  // 计算属性
  get teamCount() {
    return this.rawData.length
  }

  get groupCount() {
    return this.rawData.reduce((count, team) => {
      return count + (team.children ? team.children.length : 0)
    }, 0)
  }

  get weekCount() {
    return this.weekColumns.length
  }

  /**
   * 数据截止时间
   */
  get dataDeadline() {
    return moment().format('YYYY-MM-DD')
  }

  /**
   * 半年时间进度
   */
  get halfYearProgress() {
    return PerformanceCalculationUtils.getHalfYearProgress()
  }

  /**
   * 当前月份显示格式 (例如: 2025-08)
   */
  get currentMonthDisplay() {
    return moment(this.queryDate).format('YYYY-MM')
  }

  /**
   * 半年时间段选项
   */
  get halfYearOptions() {
    return PerformanceCalculationUtils.generateHalfYearOptions()
  }

  /**
   * 团队选项
   */
  get teamOptions() {
    return PerformanceCalculationUtils.extractTeamOptions(this.allTableRows)
  }

  /**
   * 动态标题
   */
  get dynamicTitle() {
    if (this.selectedPeriod) {
      const option = this.halfYearOptions.find(opt => opt.value === this.selectedPeriod)
      if (option) {
        return `捷展团队${option.label}目标进度表`
      }
    }
    return '捷展团队半年目标进度表'
  }

  /**
   * 获取当前半年选项
   */
  getCurrentHalfYearOption() {
    return PerformanceCalculationUtils.getCurrentHalfYearOption()
  }

  async mounted() {
    await this.loadData()
  }


  /**
   * 加载数据
   */
  async loadData(queryDate = null) {
    this.loading = true
    try {
      // 使用传入的查询日期或默认日期
      const dateToQuery = queryDate || this.queryDate

      // 从API获取半年目标数据
      const result = await request.get('reports/team_performances', {
        params: {
          date: dateToQuery,
          period: 'half_year'
        }
      })

      console.log('result', result);

      // 保存原始数据
      this.rawData = result

      // 处理数据生成表格所需格式
      const processedData = PerformanceCalculationUtils.processHalfYearlyTableData(
        this.rawData,
        dateToQuery
      )

      console.log('processedData', processedData);

      this.allTableRows = processedData.rows
      this.weekColumns = processedData.weekColumns

      // 应用团队过滤
      this.applyTeamFilter()

      // 更新查询日期
      this.queryDate = dateToQuery

      // 更新时间
      this.updateTime = moment().format('YYYY-MM-DD HH:mm:ss')

    } catch (error) {
      this.$message.error('加载数据失败，请重试')
    } finally {
      this.loading = false
    }
  }

  /**
   * 处理团队筛选变化
   */
  handleTeamFilterChange() {
    this.applyTeamFilter()
  }

  /**
   * 应用团队过滤
   */
  applyTeamFilter() {
    if (!this.selectedTeam || this.selectedTeam === '') {
      // 显示所有团队
      this.tableRows = [...this.allTableRows]
    } else {
      // 只显示选中的团队
      this.tableRows = this.allTableRows.filter(row => row.teamName === this.selectedTeam)
    }
  }

  /**
   * 时间段变化处理
   */
  handlePeriodChange() {
    if (!this.selectedPeriod) {
      this.$message.warning('请先选择时间段')
      return
    }
    this.loadData(this.selectedPeriod)
  }

  /**
   * 日期变化处理
   */
  handleDateChange(date) {
    if (date) {
      this.queryDate = date
      this.loadData()
    }
  }

  /**
   * 导出Excel
   */
  async exportToExcel() {
    if (!this.tableRows || this.tableRows.length === 0) {
      this.$message.warning('暂无数据可导出')
      return
    }

    this.exporting = true
    try {
      // 生成Excel数据和合并信息
      const { data: excelData, merges } = this.generateExcelDataWithMerges()

      // 创建工作簿
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet(excelData)

      // 设置合并单元格
      if (merges && merges.length > 0) {
        ws['!merges'] = merges
      }

      // 设置列宽
      const colWidths = this.generateColumnWidths()
      ws['!cols'] = colWidths

      // 添加工作表
      XLSX.utils.book_append_sheet(wb, ws, '半年业绩目标进度表')

      // 导出文件
      const fileName = `半年业绩目标进度表-${this.getFormattedPeriod()}.xlsx`
      XLSX.writeFile(wb, fileName)

      this.$message.success('导出成功')
    } catch (error) {
      console.error('导出失败:', error)
      this.$message.error('导出失败，请重试')
    } finally {
      this.exporting = false
    }
  }

  /**
   * 生成Excel数据（带合并单元格信息）
   */
  generateExcelDataWithMerges() {
    const data = []
    const merges = []

    // 构建表头
    const header = [
      '团队名称', '队长', '组长', '组员', '跟单员', '半年度',
      '团队目标', '团队达成', '团队达成率',
      '小组目标', '小组达成', '小组达成率',
      '半年度目标', '半年度达成', '半年达成率', '排名', '缺口目标',
      '新客户达成', '新客户占比', '老客户占比'
    ]

    data.push(header)

    // 生成数据行
    this.tableRows.forEach((row, rowIndex) => {
      const dataRow = [
        row.teamName || '',
        row.teamLeader || '',
        row.groupLeader || '',
        row.teamMembers || '',
        row.merchandiser || '',
        row.halfYearPeriod || '',
        this.formatCurrency(row.teamHalfYearOrderTarget),
        this.formatCurrency(row.teamHalfYearOrderActual),
        this.formatPercentage(row.teamHalfYearAchievementRate),
        row.personType === 'team_leader' ? '-' : this.formatCurrency(row.groupHalfYearOrderTarget),
        row.personType === 'team_leader' ? '-' : this.formatCurrency(row.groupHalfYearOrderActual),
        row.personType === 'team_leader' ? '-' : this.formatPercentage(row.groupHalfYearAchievementRate),
        this.formatCurrency(row.halfYearOrderTarget),
        this.formatCurrency(row.halfYearOrderActual),
        this.formatPercentage(row.halfYearAchievementRate),
        row.ranking || '',
        this.formatGapTarget(row.gapTarget),
        this.formatCurrency(row.newCustomerAchievement),
        this.formatPercentage(row.newCustomerRatio),
        this.formatPercentage(row.oldCustomerRatio)
      ]

      data.push(dataRow)

      // 团队级别合并：团队名称、队长、队达成率
      if (row.isFirstInTeam && row.teamRowspan > 1) {
        // 团队名称列 (c: 0)
        merges.push({
          s: { r: rowIndex + 1, c: 0 },
          e: { r: rowIndex + 1 + row.teamRowspan - 1, c: 0 }
        })

        // 队长列 (c: 1)
        merges.push({
          s: { r: rowIndex + 1, c: 1 },
          e: { r: rowIndex + 1 + row.teamRowspan - 1, c: 1 }
        })

        // 团队业绩组列合并 (c: 6,7,8)
        for (let col = 6; col <= 8; col++) {
          merges.push({
            s: { r: rowIndex + 1, c: col },
            e: { r: rowIndex + 1 + row.teamRowspan - 1, c: col }
          })
        }
      }

      // 组级别合并：组长、跟单员
      if (row.isFirstInGroup && row.groupRowspan > 1) {
        // 组长列 (c: 2)
        merges.push({
          s: { r: rowIndex + 1, c: 2 },
          e: { r: rowIndex + 1 + row.groupRowspan - 1, c: 2 }
        })

        // 跟单员列 (c: 4)
        merges.push({
          s: { r: rowIndex + 1, c: 4 },
          e: { r: rowIndex + 1 + row.groupRowspan - 1, c: 4 }
        })

        // 小组业绩组列合并 (c: 9,10,11)
        for (let col = 9; col <= 11; col++) {
          merges.push({
            s: { r: rowIndex + 1, c: col },
            e: { r: rowIndex + 1 + row.groupRowspan - 1, c: col }
          })
        }
      }
    })

    return { data, merges }
  }

  /**
   * 生成列宽设置
   */
  generateColumnWidths() {
    const widths = [12, 12, 12, 15, 12, 10, 15, 15, 12, 15, 15, 12, 15, 15, 12, 8, 15, 15, 12, 12]
    return PerformanceCalculationUtils.generateExcelColumnWidths(widths)
  }

  /**
   * 获取格式化的时间段用于文件名
   */
  getFormattedPeriod() {
    if (this.selectedPeriod) {
      const option = this.halfYearOptions.find(opt => opt.value === this.selectedPeriod)
      if (option) {
        return option.label.replace('年', '').replace('半年', 'H')
      }
    }
    return moment().format('YYYY-上半年')
  }

  /**
   * 打印表格（预留功能）
   */
  printTable() {
    window.print()
  }

  // ====================
  // 表格展示相关方法
  // ====================

  // Element UI 表格相关方法
  getRowClassName({ rowIndex }) {
    const row = this.tableRows[rowIndex]
    if (!row) return ''

    const classes = []
    // 根据需要添加行样式类

    return classes.join(' ')
  }

  getCellClassName({ column }) {
    // 使用列的property来识别列类型，而不是索引
    const prop = column.property

    // 基础信息列
    const basicInfoProps = ['teamName', 'teamLeader', 'groupLeader', 'teamMembers', 'merchandiser', 'halfYearPeriod']
    if (basicInfoProps.includes(prop)) {
      return 'basic-info'
    }

    // 团队业绩列
    const teamPerformanceProps = ['teamHalfYearOrderTarget', 'teamHalfYearOrderActual', 'teamHalfYearAchievementRate']
    if (teamPerformanceProps.includes(prop)) {
      return 'team-performance'
    }

    // 小组业绩列
    const groupPerformanceProps = ['groupHalfYearOrderTarget', 'groupHalfYearOrderActual', 'groupHalfYearAchievementRate']
    if (groupPerformanceProps.includes(prop)) {
      return 'group-performance'
    }

    // 个人业绩列
    const corePerformanceProps = ['halfYearOrderTarget', 'halfYearOrderActual', 'halfYearAchievementRate', 'ranking']
    if (corePerformanceProps.includes(prop)) {
      return 'core-performance'
    }

    // 缺口目标特殊列
    if (prop === 'gapTarget') {
      return 'gap-target'
    }

    // 客户分析列
    const customerAnalysisProps = ['newCustomerAchievement', 'newCustomerRatio', 'oldCustomerRatio']
    if (customerAnalysisProps.includes(prop)) {
      return 'customer-analysis'
    }

    return ''
  }

  // 合并单元格方法
  spanMethod({ column, rowIndex }) {
    const currentRow = this.tableRows[rowIndex]
    if (!currentRow) return [1, 1]
    const prop = column.property

    // 基础信息组内的团队名称列合并
    if (prop === 'teamName') {
      if (currentRow.isFirstInTeam) {
        return [currentRow.teamRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 基础信息组内的队长列合并
    if (prop === 'teamLeader') {
      if (currentRow.isFirstInTeam) {
        return [currentRow.teamRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 基础信息组内的组长列合并
    if (prop === 'groupLeader') {
      if (currentRow.personType === 'team_leader' || currentRow.isFirstInGroup) {
        return [currentRow.groupRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 基础信息组内的跟单员列合并
    if (prop === 'merchandiser') {
      if (currentRow.personType === 'team_leader' || currentRow.isFirstInGroup) {
        return [currentRow.groupRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 团队业绩相关列的合并（团队级别数据）
    const teamLevelProps = ['teamHalfYearOrderTarget', 'teamHalfYearOrderActual', 'teamHalfYearAchievementRate']
    if (teamLevelProps.includes(prop)) {
      if (currentRow.isFirstInTeam) {
        return [currentRow.teamRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    // 小组业绩相关列的合并（小组级别数据）
    const groupLevelProps = ['groupHalfYearOrderTarget', 'groupHalfYearOrderActual', 'groupHalfYearAchievementRate']
    if (groupLevelProps.includes(prop)) {
      if (currentRow.personType === 'team_leader') {
        // 队长行，这些列不需要显示具体数值，但需要占位
        return [1, 1]
      } else if (currentRow.isFirstInGroup) {
        return [currentRow.groupRowspan || 1, 1]
      } else {
        return [0, 0] // 隐藏合并的行
      }
    }

    return [1, 1] // 默认不合并
  }

  /**
   * 格式化百分比
   */
  formatPercentage(value) {
    return PerformanceCalculationUtils.formatPercentage(value)
  }

  /**
   * 格式化数值
   */
  formatNumber(value) {
    return PerformanceCalculationUtils.formatNumber(value)
  }

  /**
   * 格式化金额（万元单位）
   */
  formatCurrency(value) {
    return PerformanceCalculationUtils.formatCurrency(value)
  }

  formatGapTarget(value) {
    if (value <= 0) {
      return '已完成'
    }
    return PerformanceCalculationUtils.formatCurrency(value)
  }

  getGroupLeaderCellStyle() {
    // 移除队长行的特殊字体颜色，统一使用黑色
    return {}
  }

  getTeamMembersCellStyle() {
    // 移除队长行的特殊字体颜色，统一使用黑色
    return {}
  }

}
</script>

<style lang="scss" scoped>
// 引入公共样式变量
@import './performance-table-styles.scss';
</style>
